
import { createTheme } from '@mui/material/styles';
import { AppColors } from 'common/src/types';

export const MuiTheme = (colors: AppColors) => {
    const borderRadius = '16px';

    const theme = createTheme({
        palette: {
            mode: 'light',
            primary: {
                main: colors.primary,
                dark: colors.primary,
                contrastText: colors.white,
                light: colors.primary + '80',
            },
            secondary: {
                main: colors.star,
                dark: colors.star,
                light: colors.star + '80',
                contrastText: colors.black,
            },
            error: {
                main: colors.danger,
                dark: colors.danger,
                contrastText: colors.white,
                light: colors.danger + '80',
            },
            warning: {
                main: colors.star,
                dark: colors.star,
                light: colors.star + '80',
                contrastText: colors.black,
            },
            info: {
                main: colors.waterColor,
                dark: colors.waterColor,
                contrastText: colors.black,
                light: colors.waterColor + '80',
            },
            success: {
                main: colors.success,
                dark: colors.success,
                contrastText: colors.black,
                light: colors.success + '80',
            },
            text: {
                primary: colors.text + "!Important",
                disabled: colors.text + '40 !Important',
                secondary: colors.text + '80 !Important',
            },
            background: {
                paper: colors.card,
                default: colors.background,
            },
            divider: colors.border,
            action: {
                hover: colors.primary + '20',
                active: colors.primary + '80',
                selected: colors.primary + '30',
                disabled: colors.primary + '40',
                disabledBackground: colors.primary + '20',
            },
            common: {
                black: colors.black,
                white: colors.white,
            },
            grey: {
                50: colors.background,
                100: colors.card,
                200: colors.border,
                300: colors.placeholder,
                400: colors.text + '80',
                500: colors.text,
                600: colors.text,
                700: colors.black + '80',
                800: colors.black,
                900: colors.black,
            },
            // // Cores personalizadas adicionais
            // star: colors.star,
            // shadow: colors.shadow,
            // polyline: colors.polyline,
            // waterColor: colors.waterColor,
            // placeholder: colors.placeholder,
            // notification: colors.notification,
            // gradientCard: colors.gradientCard,
            // gradientBorder: colors.gradientBorder,
            // gradientBackground: colors.gradientBackground,
            // transparent: colors.transparent,
        },
        typography: {
            allVariants: {
                color: colors.text + "!Important",
            },
            h1: {
                '@media (max-width: 600px)': {
                    fontSize: '2.5rem',
                },
            },
            h2: {
                '@media (max-width: 600px)': {
                    fontSize: '2rem',
                },
            },
            h3: {
                '@media (max-width: 600px)': {
                    fontSize: '1.75rem',
                },
            },
            h4: {
                '@media (max-width: 600px)': {
                    fontSize: '1.5rem',
                },
            },
            h5: {
                '@media (max-width: 600px)': {
                    fontSize: '1.25rem',
                },
            },
            h6: {
                '@media (max-width: 600px)': {
                    fontSize: '1.1rem',
                },
            },
            subtitle1: {
                '@media (max-width: 600px)': {
                    fontSize: '0.95rem',
                },
            },
            subtitle2: {
                '@media (max-width: 600px)': {
                    fontSize: '0.85rem',
                },
            },
            body1: {
                '@media (max-width: 600px)': {
                    fontSize: '0.9rem',
                },
            },
            body2: {
                '@media (max-width: 600px)': {
                    fontSize: '0.8rem',
                },
            },
            button: {
                '@media (max-width: 600px)': {
                    fontSize: '0.8rem',
                },
            },
            caption: {
                '@media (max-width: 600px)': {
                    fontSize: '0.7rem',
                },
            },
            overline: {
                '@media (max-width: 600px)': {
                    fontSize: '0.65rem',
                },
            },
        },
        components: {
            MuiCssBaseline: {
                styleOverrides: {
                    '*': {
                        scrollbarWidth: 'thin',
                        scrollbarColor: `${colors.text} ${colors.card}`,
                    },
                    'input:-webkit-autofill': {
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                        transition: 'background-color 5000s ease-in-out 0s',
                    },
                    'input:-webkit-autofill:hover': {
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                    },
                    'input:-webkit-autofill:focus': {
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                    },
                    'input:-webkit-autofill:active': {
                        WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        WebkitTextFillColor: `${colors.text} !important`,
                    },
                    'input:-moz-autofill': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                    },
                    'input:-moz-autofill:hover': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    'input:-moz-autofill:focus': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    'input:autofill': {
                        background: `${colors.card} !important`,
                        color: `${colors.text} !important`,
                    },
                    '.sketch-picker > :nth-child(3)': {
                        display: 'none !Important',
                    },
                    '.Component-horizontalScrollContainer-12': {
                        backgroundColor: colors.card,
                        color: colors.text,
                        '&::-webkit-scrollbar': {
                            height: 8,
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: colors.text,
                            borderRadius: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                            backgroundColor: colors.card,
                        },
                        scrollbarColor: `${colors.text} ${colors.card}`,
                        scrollbarWidth: 'thin',
                    },
                    '.MuiTableHead-root': {
                        backgroundColor: colors.card,
                    },
                    '.MuiTableHead-root th': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-iconDirectionDesc': {
                        backgroundColor: colors.card,
                        color: colors.text,
                    },
                    '.MuiTableCell-head': {
                        backgroundColor: colors.card,
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-root': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-root.Mui-active': {
                        color: colors.text,
                    },
                    '.MuiTableSortLabel-icon': {
                        color: colors.text,
                    },
                    '.MuiPaper-root .MuiTableCell-head': {
                        color: colors.text,
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiDivider: {
                styleOverrides: {
                    root: {
                    },
                },
            },
            MuiToolbar: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiTextField: {
                styleOverrides: {
                    root: {
                        "& .MuiOutlinedInput-root": {
                            backgroundColor: colors.card,
                            transition: "all 0.2s ease",

                            "& fieldset": {
                                borderWidth: "0 !important",
                            },

                            "&:hover fieldset": {
                                borderWidth: "0 !important",
                            },

                            "&.Mui-focused fieldset": {
                                borderWidth: "0 !important",
                            },

                            "&.Mui-disabled": {
                                opacity: 0.5,
                                WebkitTextFillColor: `${colors.text} !important`,

                                "& .MuiOutlinedInput-input": {
                                    WebkitTextFillColor: `${colors.text} !important`,
                                },
                            },

                            "&.Mui-error": {
                                "& fieldset": {
                                    borderWidth: "0 !important",
                                },
                            },
                        },

                        "& .MuiInputBase-input": {
                            fontWeight: 500,
                            fontSize: "16px",
                            color: `${colors.text} !important`,
                            fontFamily: "'Roboto', sans-serif",
                            padding: "16.5px 14px",

                            "&::placeholder": {
                                color: colors.placeholder,
                                opacity: 1,
                            },

                            "&.Mui-disabled": {
                                WebkitTextFillColor: `${colors.text} !important`,
                                color: `${colors.text} !important`,
                                opacity: 0.5,
                            },
                        },

                        "& .MuiInputLabel-root": {
                            color: colors.text,

                            "&.Mui-focused": {
                                color: colors.primary,
                            },

                            "&.Mui-disabled": {
                                opacity: 0.5,
                            },

                            "&.Mui-error": {
                                color: colors.danger,
                            },
                        },

                        "& .MuiFormHelperText-root": {
                            marginLeft: 0,
                            marginTop: "4px",

                            "&.Mui-error": {
                                color: colors.danger,
                            },
                        },

                        // Estilos para autofill
                        "& input:-webkit-autofill": {
                            WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                            WebkitTextFillColor: `${colors.text} !important`,
                            transition: "background-color 5000s ease-in-out 0s",
                        },

                        "& input:-webkit-autofill:hover": {
                            WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        },

                        "& input:-webkit-autofill:focus": {
                            WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        },

                        "& input:-webkit-autofill:active": {
                            WebkitBoxShadow: `0 0 0px 1000px ${colors.card} inset !important`,
                        },
                    },
                },
            },
            MuiTableHead: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        '& th': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiStepConnector: {
                styleOverrides: {
                    line: {
                        borderColor: colors.text + '40',
                    },
                },
            },
            MuiStepIcon: {
                styleOverrides: {
                    root: {
                        color: colors.text + '40',
                        '&.Mui-active': {
                            color: colors.primary,
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                        '& text': {
                            fill: colors.card, // Cor do número dentro do ícone
                        },
                    },
                },
            },
            MuiStepLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text + '80',
                        '&.Mui-active': {
                            color: colors.text,
                            fontWeight: 'bold',
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                    iconContainer: {
                        paddingRight: 8,
                    },
                    label: {
                        '&.Mui-active': {
                            color: colors.text,
                        },
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiStepper: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent',
                        padding: '24px 0',
                    },
                },
            },
            MuiStep: {
                styleOverrides: {
                    root: {
                        '&.Mui-completed': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiTableBody: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        '& td': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiPaper: {
                styleOverrides: {
                    root: {
                        padding: '5px',
                        boxShadow: 'none !important',
                        backgroundColor: colors.card,
                    },
                },
            },
            MuiTableContainer: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        scrollbarWidth: 'thin',
                        backgroundColor: colors.card,
                        scrollbarColor: `${colors.card} transparent`,
                        '&::-webkit-scrollbar': {
                            height: 8,
                        },
                        '&::-webkit-scrollbar-track': {
                            background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: colors.primary,
                            borderRadius: 8,
                        },
                    },
                },
            },
            MuiGrid: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'transparent !important',
                    },
                },
            },
            MuiTableRow: {
                styleOverrides: {
                    root: {
                        backgroundColor: colors.card,
                        border: `2px solid ${colors.border}`,
                        '&:nth-of-type(odd)': {
                            backgroundColor: colors.card,
                        },
                        '&:nth-of-type(even)': {
                            backgroundColor: colors.card,
                        },
                    },
                },
            },
            MuiTableCell: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        borderColor: colors.card,
                        backgroundColor: colors.card,
                        '& a': {
                            color: colors.text,
                        },
                        '& svg': {
                            color: colors.text,
                        },
                        '& .MuiTypography-root': {
                            color: colors.text,
                        },
                    },
                },
            },
            MuiTab: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        marginRight: '16px',
                        '&.Mui-selected': {
                            color: colors.primary,
                            fontWeight: 'bold',
                        },
                        '&.Mui-focusVisible': {
                            outline: `2px solid ${colors.primary}`,
                            outlineOffset: '2px',
                        },
                    },
                },
            },
            MuiSelect: {
                styleOverrides: {
                    root: {
                        color: `${colors.text} !important`,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: `${colors.primary + '40'} !important`,
                            borderWidth: `1px !important`,
                            transition: 'all 0.2s ease !important',
                        },

                        '&:hover': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `1px !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '&.Mui-focused': {
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `2px !important`,
                                boxShadow: `0 0 0 2px ${colors.primary}20 !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '&:focus-visible': {
                            outline: `none !important`,
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary} !important`,
                                borderWidth: `2px !important`,
                                boxShadow: `0 0 0 3px ${colors.primary}40 !important`,
                            },
                            '& .MuiSelect-icon': {
                                color: `${colors.primary} !important`,
                            },
                        },

                        '& .MuiSelect-select': {
                            '&:focus-visible': {
                                outline: `none !important`,
                                backgroundColor: 'transparent !important',
                                '& + .MuiOutlinedInput-notchedOutline': {
                                    borderColor: `${colors.primary} !important`,
                                    borderWidth: `2px !important`,
                                    boxShadow: `0 0 0 3px ${colors.primary}40 !important`,
                                },
                            },
                        },

                        '& .MuiSelect-icon': {
                            color: `${colors.primary + '80'} !important`,
                            transition: 'color 0.2s ease !important',

                            '&.MuiSelect-iconOpen': {
                                color: `${colors.primary} !important`,
                                transform: 'rotate(180deg) !important',
                            },
                        },

                        '&.Mui-disabled': {
                            opacity: '0.5 !important',
                            '& .MuiSelect-icon': {
                                color: `${colors.primary + '40'} !important`,
                            },
                            '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: `${colors.primary + '20'} !important`,
                            },
                        },
                    },
                },
            },
            MuiMenu: {
                styleOverrides: {
                    paper: {
                        marginTop: '8px !important',
                        borderRadius: '8px !important',
                        backgroundImage: 'none !important',
                        backgroundColor: `${colors.card} !important`,
                        boxShadow: `0 8px 24px ${colors.shadow} !important`,
                    },
                },
            },

            MuiMenuItem: {
                styleOverrides: {
                    root: {
                        margin: '2px 0 !important',
                        padding: '10px 16px !important',
                        borderRadius: '6px !important',
                        color: `${colors.text} !important`,
                        transition: 'all 0.2s ease !important',

                        '&:hover': {
                            backgroundColor: `${colors.primary + '20'} !important`,
                            color: `${colors.primary} !important`,
                        },

                        '&.Mui-selected': {
                            backgroundColor: `${colors.primary + '30'} !important`,
                            color: `${colors.primary} !important`,

                            '&:hover': {
                                backgroundColor: `${colors.primary + '40'} !important`,
                            },
                        },

                        '&.Mui-focusVisible': {
                            backgroundColor: `${colors.primary + '20'} !important`,
                            outline: `2px solid ${colors.primary} !important`,
                            outlineOffset: '-2px !important',
                        },
                    },
                },
            },
            MuiTabs: {
                styleOverrides: {
                    indicator: {
                        backgroundColor: colors.primary,
                    },
                },
            },
            MuiInputBase: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                    input: {
                        color: colors.text,
                    },
                },
            },
            MuiInput: {
                styleOverrides: {
                    root: {
                        '&:before': {
                            borderBottom: `1px solid ${colors.text}`,
                        },
                        '&:hover:not(.Mui-disabled):before': {
                            borderBottom: `1px solid ${colors.text}`,
                        },
                        '&.Mui-focused:after': {
                            borderBottom: `1px solid ${colors.primary}`,
                        },
                    },
                },
            },
            MuiContainer: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'red',
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                    }
                },
            },
            //     styleOverrides: {
            //         root: {
            //             borderRadius: '6px',
            //             textTransform: 'none',
            //             fontWeight: 'bold',
            //             transition: 'all 0.3s ease',
            //             '&.Mui-disabled': {
            //                 backgroundColor: `${colors.text}20 !important`,
            //                 color: `${colors.text}60 !important`,
            //                 borderColor: `${colors.text}20 !important`,
            //                 cursor: 'not-allowed',
            //                 pointerEvents: 'all !important',
            //                 '&:hover': {
            //                     backgroundColor: `${colors.text}20 !important`,
            //                 },
            //             },
            //         },
            //         contained: {
            //             backgroundColor: colors.primary,
            //             color: colors.card,
            //             '&:hover': {
            //                 backgroundColor: colors.primary + 'DD',
            //                 transform: 'translateY(-2px)',
            //                 boxShadow: `0 4px 12px ${colors.primary}40`,
            //             },
            //             '&:active': {
            //                 transform: 'translateY(0)',
            //             },
            //             '&.Mui-disabled': {
            //                 backgroundColor: `${colors.text}20 !important`,
            //                 color: `${colors.text}60 !important`,
            //                 boxShadow: 'none',
            //             },
            //         },
            //         outlined: {
            //             borderColor: colors.primary,
            //             color: colors.primary,
            //             '&:hover': {
            //                 backgroundColor: colors.primary + '15',
            //                 borderColor: colors.primary,
            //             },
            //             '&.Mui-disabled': {
            //                 borderColor: `${colors.text}20 !important`,
            //                 color: `${colors.text}60 !important`,
            //             },
            //         },
            //         text: {
            //             color: colors.text,
            //             '&:hover': {
            //                 backgroundColor: colors.primary + '15',
            //             },
            //             '&.Mui-disabled': {
            //                 color: `${colors.text}60 !important`,
            //             },
            //         },
            //     },
            // },
            MuiSvgIcon: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                },
            },
            MuiFormLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&.Mui-focused': {
                            color: colors.primary,
                        },
                    },
                },
            },
            MuiInputLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                },
            },
            MuiCardActions: {
                styleOverrides: {
                    root: ({ theme }) => ({
                        display: 'flex',
                        transition: 'ease',
                        gap: theme.spacing(2),
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }),
                },
            },
            MuiCard: {
                styleOverrides: {
                    root: {
                        gap: 10,
                        width: '100%',
                        // maxWidth: '90%',
                        height: 'auto',
                        margin: 'auto',
                        '@media (min-width:600px)': {
                            maxWidth: 400,
                        },
                        '@media (min-width:900px)': {
                            maxWidth: 600,
                        },
                        padding: '24px',
                        transition: 'ease',
                        borderRadius,
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(5px)',
                        boxShadow: `0 4px 20px ${colors.shadow}`,
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                    },
                },
            },
            MuiOutlinedInput: {
                styleOverrides: {
                    root: {
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.text,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.text,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: colors.primary,
                        },
                    },
                    input: {
                        color: colors.text,
                    },
                },
            },
            MuiModal: {
                styleOverrides: {
                    root: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    },
                },
            },
            MuiDialog: {
                styleOverrides: {
                    paper: {
                        gap: 10,
                        borderRadius,
                        width: '100%',
                        height: 'auto',
                        margin: 'auto',
                        maxWidth: '90%',
                        padding: '24px',
                        overflow: 'hidden',
                        transition: 'ease',
                        boxSizing: 'border-box',
                        backdropFilter: 'blur(5px)',
                        boxShadow: `0 4px 20px ${colors.shadow}`,
                        background: `linear-gradient(135deg, ${colors.card} 0%, ${colors.card}CC 100%)`,
                        '@media (min-width:600px)': {
                            maxWidth: 400,
                        },
                        '@media (min-width:900px)': {
                            maxWidth: 600,
                        },
                    },
                },
            },
            MuiDialogTitle: {
                styleOverrides: {
                    root: {
                        fontWeight: 'bold',
                        textAlign: 'center',
                        color: colors.text,
                    },
                },
            },
            MuiIconButton: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&:hover': {
                            color: colors.text,
                            backgroundColor: 'transparent',
                        },
                        '&.Mui-disabled': {
                            color: `${colors.text}66`,
                            backgroundColor: 'transparent',
                        },
                        '&:active': {
                            color: colors.primary
                        },
                    },
                },
            },
            MuiInputAdornment: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                    },
                    positionStart: {
                        color: colors.text,
                    },
                    positionEnd: {
                        color: colors.text,
                    },
                },
            },
            MuiDialogContentText: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        textAlign: 'center',
                    },
                },
            },
            MuiDialogActions: {
                styleOverrides: {
                    root: {
                        justifyContent: 'center',
                    },
                },
            },
            MuiBackdrop: {
                styleOverrides: {
                    root: {
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    },
                },
            },
            MuiTableSortLabel: {
                styleOverrides: {
                    root: {
                        color: colors.text,
                        '&.Mui-active': {
                            color: colors.text,
                        },
                        '&:hover': {
                            color: colors.text,
                        },
                        '& .MuiTableSortLabel-icon': {
                            color: colors.text,
                        },
                    },
                },
            },

            MuiButton: {
                styleOverrides: {
                    root: {
                        borderRadius: '6px',
                        textTransform: 'none',
                        fontWeight: 'bold',
                        transition: 'all 0.3s ease',
                        '&.Mui-disabled': {
                            backgroundColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                            borderColor: `${colors.text}20 !important`,
                            cursor: 'not-allowed',
                            pointerEvents: 'all !important',
                            '&:hover': {
                                backgroundColor: `${colors.text}20 !important`,
                            },
                        },
                    },
                    contained: {
                        backgroundColor: colors.primary,
                        color: colors.white,
                        '&:hover': {
                            backgroundColor: colors.primary + 'DD',
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${colors.primary}40`,
                        },
                        '&:active': {
                            transform: 'translateY(0)',
                        },
                        '&.Mui-disabled': {
                            backgroundColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                            boxShadow: 'none',
                        },
                    },
                    outlined: {
                        color: colors.primary,
                        borderColor: colors.primary,
                        '&:hover': {
                            backgroundColor: colors.primary + '15',
                            borderColor: colors.primary,
                        },
                        '&.Mui-disabled': {
                            borderColor: `${colors.text}20 !important`,
                            color: `${colors.text}60 !important`,
                        },
                        '& .MuiButton-startIcon, & .MuiButton-endIcon': {
                            color: 'inherit',
                            '& svg': {
                                color: 'inherit',
                            },
                        },
                    },
                    text: {
                        color: colors.text,
                        '&:hover': {
                            backgroundColor: colors.primary + '15',
                        },
                        '&.Mui-disabled': {
                            color: `${colors.text}60 !important`,
                        },
                    },
                },
            },
        },
    });

    Object.entries(colors).forEach(([key, value]) => {
        (theme as any)[key] = value;
    });

    return theme;
};